import React from "react";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import Fonts from "../../ui/global/Fonts";
import { GameMetrics } from "../game/components/GameGallery";
import { useConfig } from "../../utils/config";

const Card = styled.div<{ $colormode: 'light' | 'dark'; $isClickable?: boolean }>`
    max-width: 100%;
    width: 100%;
    border-radius: 4px;
    overflow: hidden;
    cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
    transition: all 0.2s ease-in-out;

    &:hover {
        transform: ${props => props.$isClickable ? 'scale(1.05)' : 'none'};
    }
`;

const ProjectImageContainer = styled.div<{ $type: 'Game' | 'Art' | 'Animation'; $colormode: 'light' | 'dark'; $hasImage: boolean }>`
    position: relative;
    width: 100%;
    padding-top: ${props => props.$type === 'Game' ? '66%' : '100%'}; 
    border-radius: 2px;
    background-color: ${Colors.white};
    background-image: ${props => (props.$type === 'Animation' && props.$hasImage) ? 'url("/assets/transparent-background.svg")' : 'none'};
    background-size: cover;
    background-repeat: no-repeat;
    border: 1px solid ${props => props.$colormode === 'light' ? Colors.grey2 : Colors.grey8};
    overflow: hidden;
    transition: all 0.2s ease-in-out;

    &:hover {
        border: 1px solid ${props => props.$colormode === 'light' ? Colors.grey3 : Colors.grey7};
    }
`;

const StyledImage = styled.div<{ $imageUrl?: string }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => props.$imageUrl ? `url(${props.$imageUrl})` : 'none'};
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
`;

const StyledVideo = styled.video`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none; /* This prevents the video from capturing the right-click menu entirely */
`;

const ProjectLabelsContainer = styled.div<{ $colormode: 'light' | 'dark'; $type: 'Game' | 'Art' | 'Animation' }>`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: ${props => props.$type === 'Game' ? '6px 0px 12px 10px' : '6px 10px 12px 10px'};
`;

const NameContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`;

const Label = styled.p`
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-size: 14px;
    line-height: 1.4em;
    max-width: 100%;
    white-space: nowrap;
    margin: 4px 0 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProjectName = styled(Label)<{ $colormode: 'light' | 'dark' }>`
    color: ${props => props.$colormode === 'light' ? Colors.grey8 : Colors.grey2};
    ${Card}:hover & {
        color: ${props => props.$colormode === 'light' ? Colors.black : Colors.white};
    }
`;

export interface ProjectCardProps {
    type: 'Game' | 'Art' | 'Animation';
    projectName: string;
    username: string;
    imageUrl?: string;
    colormode: 'light' | 'dark';
    artType?: '2D' | '3D';
    onClick?: () => void;
    gameMetrics?: GameMetrics;
    updatedAt?: string;
    isClickable?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
    type,
    projectName,
    imageUrl,
    colormode,
    onClick,
    isClickable,
}) => {
    const isVideo = imageUrl?.toLowerCase().endsWith('.mp4') || 
                    imageUrl?.toLowerCase().endsWith('.webm') ||
                    imageUrl?.includes('animation_video');

    const hasMedia = !!imageUrl;
    // Use explicit isClickable prop if provided, otherwise fallback to checking onClick
    const finalIsClickable = isClickable !== undefined ? isClickable : !!onClick;

    return (
        <Card 
            onClick={onClick} 
            $colormode={colormode}
            $isClickable={finalIsClickable}
            draggable={type !== 'Game' ? "false" : "true"}
        >
            <ProjectImageContainer 
                $type={type} 
                $colormode={colormode} 
                $hasImage={hasMedia}
            >
                {isVideo ? (
                    <StyledVideo 
                        src={imageUrl} 
                        autoPlay 
                        loop 
                        muted 
                        playsInline 
                        disablePictureInPicture
                        controlsList="nodownload"
                    />
                ) : (
                    <StyledImage 
                        $imageUrl={imageUrl} 
                        role="img"
                        aria-label={projectName}
                    />
                )}
            </ProjectImageContainer>
            <ProjectLabelsContainer $colormode={colormode} $type={type}>
                <NameContainer>
                    <ProjectName $colormode={colormode}>{projectName}</ProjectName>
                </NameContainer>
            </ProjectLabelsContainer>
        </Card>
    );
};

export default ProjectCard;